
import { useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useIsMobile } from "@/hooks/use-mobile";
import { ImageIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ActivityImageProps {
  imageUrl?: string;
  imageUrls?: string[];
  title: string;
}

const ActivityImage = ({ imageUrl, imageUrls = [], title }: ActivityImageProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  
  // Combine primary imageUrl with any additional imageUrls
  // Use a Set to ensure all images are unique
  const uniqueImages = Array.from(new Set([imageUrl, ...imageUrls].filter(Boolean)));
  
  console.debug('Gallery images:', {
    length: uniqueImages.length,
    images: uniqueImages
  });
  
  // If there's no images at all, show a placeholder
  if (!imageUrl && uniqueImages.length === 0) {
    return (
      <div className="relative rounded-t-lg md:rounded-l-lg md:rounded-tr-none md:w-1/3 bg-gray-100 flex items-center justify-center aspect-video">
        <ImageIcon className="w-12 h-12 text-gray-400" />
      </div>
    );
  }

  const handleImageClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent parent card click handler from firing
    e.stopPropagation();
    
    // Only open dialog if we have multiple images
    if (uniqueImages.length > 1) {
      setOpen(true);
    }
  };

  return (
    <div 
      className="relative rounded-t-lg md:rounded-l-lg md:rounded-tr-none md:w-1/3" 
      onClick={(e) => e.stopPropagation()}
    >
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <div className="cursor-pointer" onClick={handleImageClick}>
            <div className="aspect-video relative overflow-hidden flex items-center justify-center">
              <img
                src={imageUrl}
                alt={title}
                className="object-cover w-full h-full"
              />
              
              {/* Show a gallery indicator if we have multiple images */}
              {uniqueImages.length > 1 && (
                <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded-md text-xs flex items-center">
                  <ImageIcon className="w-3 h-3 mr-1" />
                  {uniqueImages.length}
                </div>
              )}
            </div>
          </div>
        </DialogTrigger>
        
        {/* Image Gallery Dialog */}
        {uniqueImages.length > 1 && (
          <DialogContent className="max-w-3xl sm:max-w-screen-md p-0 bg-black border-none">
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute top-4 right-4 z-50 bg-black/50 hover:bg-black/70 text-white rounded-full"
              onClick={() => setOpen(false)}
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close gallery</span>
            </Button>
            
            <Carousel className="w-full">
              <CarouselContent>
                {/* Skip the first image in the carousel since it's already shown on the card */}
                {uniqueImages.slice(1).map((image, index) => (
                  <CarouselItem key={index}>
                    <div className="flex items-center justify-center p-1 h-[500px] w-full">
                      <img 
                        src={image} 
                        alt={`${title} - Image ${index + 2}`} // +2 because we're skipping first and 0-indexed
                        className="max-h-full max-w-full object-contain w-[500px] h-[500px]"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-2 sm:left-4" />
              <CarouselNext className="right-2 sm:right-4" />
            </Carousel>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default ActivityImage;
